import {
  ApprovalStatusEnum,
  IApprovalRespSchema,
  IQuoteRespSchema,
  QuoteStatusEnum,
} from '@monetize/types/app';
import { useState } from 'react';
import { MdCheck } from 'react-icons/md';
import {
  MBox,
  MButton,
  MCenterModal,
  MFlex,
  MFormField,
  MIcon,
  MStack,
  MSwitch,
  MText,
} from '../../../../../components/Monetize';
import { DateDisplay } from '../../../../../components/Monetize/DateDisplay';

interface QuoteEditConfirmationModalProps {
  quote: IQuoteRespSchema;
  isEsignEnabled: boolean;
  canRetainApprovals: boolean;
  onClose: () => void;
  onTransitionStatus: (retainApprovalIds?: string[]) => Promise<void>;
}

export const QuoteEditConfirmationModal = ({
  quote,
  isEsignEnabled,
  canRetainApprovals,
  onClose,
  onTransitionStatus,
}: QuoteEditConfirmationModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [retainApprovals, setRetainApprovals] = useState(false);

  const hasApprovedApprovals = quote.approvals.some(
    (approval) => approval.status === ApprovalStatusEnum.APPROVED,
  );

  const handleConfirm = async () => {
    setIsLoading(true);
    const retainApprovalIds = quote.approvals
      .filter(({ status }) => status === ApprovalStatusEnum.APPROVED)
      .map(({ id }) => id);
    await onTransitionStatus(retainApprovals ? retainApprovalIds : undefined);
    setIsLoading(false);
  };

  return (
    <MCenterModal
      isOpen
      onClose={onClose}
      modalTitle="Are you sure you want to edit this quote?"
      size="lg"
      modalContentProps={{ 'data-testid': 'edit-quote-modal' } as any}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="end" flex={1}>
          <MButton onClick={onClose} variant="cancel" minW="auto">
            Cancel
          </MButton>
          <MButton
            variant="primary"
            isLoading={isLoading}
            onClick={handleConfirm}
            minW="auto"
          >
            Confirm
          </MButton>
        </MStack>
      )}
    >
      {canRetainApprovals && (
        <MBox mb={2}>
          <MFormField
            labelProps={{ mb: 0 }}
            isHorizontal
            label="Retain Existing Approvals"
            tooltip="You are seeing this option because you are an administrator."
          >
            <MSwitch
              size="md"
              isChecked={retainApprovals}
              onChange={(event) => setRetainApprovals(event.target.checked)}
            />
          </MFormField>
        </MBox>
      )}

      {getMessages({
        hasApprovedApprovals,
        outForSignature:
          isEsignEnabled && quote.status === QuoteStatusEnum.SENT,
        retainApprovals,
      }).map((message) => (
        <MText key={message} mb={1}>
          {message}
        </MText>
      ))}

      <MBox ml={2}>
        {quote.approvals
          .filter(
            ({ skipped, status }) =>
              skipped || status === ApprovalStatusEnum.APPROVED,
          )
          .map((approval) => (
            <ApprovalItemSummary key={approval.id} approval={approval} />
          ))}
      </MBox>
    </MCenterModal>
  );
};

function ApprovalItemSummary({ approval }: { approval: IApprovalRespSchema }) {
  const {
    name,
    teamId,
    teamName,
    approvalTimestamp,
    modifyDate,
    approverName,
    username,
  } = approval;

  const displayName = approverName ?? (teamId ? teamName : username);

  return (
    <MFlex mb={1}>
      <MFlex
        boxSize={4}
        borderRadius="50%"
        borderWidth="2px"
        backgroundColor="tGreen.approval"
        borderColor={'tGreen.approval'}
        alignItems="center"
        justifyContent="center"
        mr={1}
        mt={1}
      >
        <MIcon as={MdCheck} color="tWhite.base" />
      </MFlex>
      <MBox>
        <MText>{name}</MText>
        <MText fontSize="xs" color="tGray.darkPurple">
          {/* FIXME: BP-13096 */}
          Approved{' '}
          <DateDisplay
            as="span"
            date={approvalTimestamp || modifyDate}
            timezoneConfigKey="userTimezone"
            textProps={{
              color: 'inherit',
              fontSize: 'inherit',
              display: 'inline',
            }}
          />{' '}
          by {displayName}
        </MText>
      </MBox>
    </MFlex>
  );
}

const getMessages = ({
  outForSignature,
  hasApprovedApprovals,
  retainApprovals,
}: {
  outForSignature: boolean;
  hasApprovedApprovals: boolean;
  retainApprovals: boolean;
}) => {
  if (outForSignature && !hasApprovedApprovals) {
    return [
      'Editing this quote will cancel the eSign process and any signatures received to date will be voided.',
    ];
  }

  if (retainApprovals && hasApprovedApprovals) {
    if (outForSignature) {
      return [
        'Editing this quote will cancel the eSign process and any signatures received to date will be voided.',
        'The following approvals will be retained during editing, so long as they are not removed due to changes in the quote:',
      ];
    }
    return [
      'The following approvals will be retained during editing, so long as they are not removed due to changes in the quote:',
    ];
  }
  if (hasApprovedApprovals) {
    if (outForSignature) {
      return [
        'Editing this quote will cancel the eSign process and any signatures received to date will be voided.',
        'The following granted approvals will be also be revoked:',
      ];
    }
    return ['Editing this quote will revoke the following granted approvals:'];
  }
  return [];
};
